import type {
  DesignConfig,
  FilterClause,
  FilterOp,
  OrGroup,
  SingleClause,
  SourceFields,
} from '../api/types';
import { NUMBER_OPS, OP_LABEL, STRING_OPS } from '../api/types';
import { isFlatEditable, summarizeFilter } from '../lib/filterSummary';
import ChipInput from './ChipInput';
import FilterPreview from './FilterPreview';

interface Props {
  config: DesignConfig;
  sourceFields: SourceFields;
  storeId: string;
  onPatchMeta: (
    meta: Partial<Pick<DesignConfig, 'name' | 'priority' | 'active_from' | 'active_to'>>,
  ) => void;
  onPatchFilter: (filter: FilterClause | null) => void;
  isDefault: boolean;
}

/**
 * Internal model: a flat list of "rows", where each row is either a single
 * clause or an "or-group" (a list of single clauses joined by OR). Rows are
 * joined by AND. When serialized:
 *
 *   [single, single, single]              → AndGroup
 *   [single]                               → SingleClause
 *   []                                     → null
 *   [or-group{...}]                        → OrGroup
 *   [or-group{...}, single, ...]           → AndGroup{[OrGroup, ..., singles]}
 */
type Row = SingleClause | OrGroup;

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function asRows(filter: FilterClause | null): Row[] {
  if (filter === null) return [];
  if (filter.kind === 'single') return [filter];
  if (filter.kind === 'or') return [filter];
  return filter.clauses;
}

function fromRows(rows: Row[]): FilterClause | null {
  if (rows.length === 0) return null;
  if (rows.length === 1) return rows[0];
  return { kind: 'and', clauses: rows };
}

function fieldKindFor(field: string, sf: SourceFields): 'string' | 'number' {
  return sf.types[field] ?? 'string';
}

function opsFor(field: string, sf: SourceFields): FilterOp[] {
  return fieldKindFor(field, sf) === 'number' ? NUMBER_OPS : STRING_OPS;
}

function emptyClause(sf: SourceFields): SingleClause {
  const firstField = sf.from_source[0] ?? sf.custom[0] ?? 'brand';
  return {
    kind: 'single',
    field: firstField,
    op: 'eq',
    value: '',
  };
}

export default function FilterEditor({
  config,
  sourceFields,
  storeId,
  onPatchMeta,
  onPatchFilter,
  isDefault,
}: Props) {
  const rows = asRows(config.filter);
  const hasFilter = rows.length > 0;
  // FE-A-1: the flat row model can't represent nested logic (OR-of-ANDs, depth
  // 3+). Editing such a filter here would drop the clauses it can't model and
  // silently re-target the config, so render it READ-ONLY instead of letting a
  // save truncate it. Only reachable for filters authored outside this editor
  // (hand-edited JSON / API) — the editor itself never builds one.
  const editable = isFlatEditable(config.filter);

  const update = (next: Row[]) => {
    onPatchFilter(fromRows(next));
  };

  const addClause = () => {
    update([...rows, emptyClause(sourceFields)]);
  };

  const addOrGroup = () => {
    const orGroup: OrGroup = {
      kind: 'or',
      clauses: [emptyClause(sourceFields), emptyClause(sourceFields)],
    };
    update([...rows, orGroup]);
  };

  const removeRowAt = (idx: number) => {
    update(rows.filter((_, i) => i !== idx));
  };

  const replaceRowAt = (idx: number, next: Row) => {
    update(rows.map((r, i) => (i === idx ? next : r)));
  };

  return (
    <aside className="space-y-6">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
          Targeting
        </p>
        <h2 className="font-display text-2xl text-ff-forest">Identity & match</h2>
      </header>

      <section>
        <label className="ff-label">Name</label>
        <input
          type="text"
          value={config.name}
          disabled={isDefault}
          onChange={(e) => onPatchMeta({ name: e.target.value })}
          className={`ff-input font-mono text-sm ${
            isDefault ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          data-testid="config-name-input"
        />
        {isDefault && (
          <p className="mt-1.5 text-[11px] text-ff-mute">
            The <span className="font-mono">_default</span> config can't be renamed.
          </p>
        )}
      </section>

      <section>
        <label className="ff-label">Priority</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={1000}
            disabled={isDefault}
            value={config.priority}
            onChange={(e) => onPatchMeta({ priority: Number(e.target.value) })}
            className={`ff-input font-mono text-sm w-32 ${
              isDefault ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          />
          <p className="text-[11px] text-ff-mute leading-snug">
            Higher wins over lower. Default is locked at 0.
          </p>
        </div>
      </section>

      <section data-testid="config-active-window">
        <label className="ff-label">Active window</label>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5">
          <div>
            <label
              htmlFor="config-active-from"
              className="text-[11px] font-mono uppercase tracking-widish text-ff-mute"
            >
              From
            </label>
            <input
              id="config-active-from"
              type="datetime-local"
              disabled={isDefault}
              value={isoToLocalInput(config.active_from)}
              onChange={(e) =>
                onPatchMeta({ active_from: localInputToIso(e.target.value) })
              }
              className={`ff-input font-mono text-sm px-3 ${
                isDefault ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              data-testid="config-active-from"
            />
          </div>
          <div>
            <label
              htmlFor="config-active-to"
              className="text-[11px] font-mono uppercase tracking-widish text-ff-mute"
            >
              Until
            </label>
            <input
              id="config-active-to"
              type="datetime-local"
              disabled={isDefault}
              value={isoToLocalInput(config.active_to)}
              onChange={(e) =>
                onPatchMeta({ active_to: localInputToIso(e.target.value) })
              }
              className={`ff-input font-mono text-sm px-3 ${
                isDefault ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              data-testid="config-active-to"
            />
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-ff-mute leading-snug">
          {isDefault
            ? "_default has no window — it always applies as a catch-all."
            : 'Optional. Leave blank for unbounded. The runner skips this config when "now" is outside the window.'}
        </p>
      </section>

      <div className="ff-divider-rule" />

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="ff-label !mb-0">Match filter</p>
          {!isDefault && hasFilter && (
            <button
              type="button"
              onClick={() => onPatchFilter(null)}
              className="text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-forest"
              data-testid="filter-clear"
            >
              Clear
            </button>
          )}
        </div>

        {!hasFilter ? (
          <div className="space-y-2">
            <p className="rounded-xl border border-dashed border-ff-sand/80 bg-white/40 px-4 py-3 text-xs text-ff-mute">
              {isDefault
                ? 'The default catches every product when no other config matches.'
                : 'No filter — this config will only ever apply to a product if its priority outranks every matching one. Add a clause to gate it.'}
            </p>
            {!isDefault && (
              <button
                type="button"
                onClick={addClause}
                className="w-full rounded-xl border border-ff-forest/40 bg-ff-mint/40 px-3 py-2 text-[11px] font-mono uppercase tracking-widish text-ff-forest hover:bg-ff-mint transition-colors"
                data-testid="filter-add-first"
              >
                + Add clause
              </button>
            )}
          </div>
        ) : !editable ? (
          <div className="space-y-2" data-testid="filter-readonly">
            <p className="rounded-xl border border-ff-warning/30 bg-ff-warning-soft px-4 py-3 text-xs text-ff-warning leading-snug">
              This filter uses nested logic the visual editor can't represent, so
              it's shown read-only to avoid silently dropping part of it on save.
              Use <span className="font-mono">Clear</span> to remove it and rebuild
              here, or edit the config JSON directly.
            </p>
            <pre
              className="whitespace-pre-wrap break-words rounded-xl border border-ff-forest/20 bg-white px-4 py-3 text-xs font-mono text-ff-forest"
              data-testid="filter-readonly-summary"
            >
              {summarizeFilter(config.filter)}
            </pre>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.length > 1 && (
              <p className="text-[10px] font-mono uppercase tracking-widish text-ff-mute">
                All rows must match (AND)
              </p>
            )}
            {rows.map((row, idx) => (
              <RowEditor
                key={idx}
                idx={idx}
                row={row}
                sourceFields={sourceFields}
                onChange={(next) => replaceRowAt(idx, next)}
                onRemove={() => removeRowAt(idx)}
                showAndLabel={idx > 0}
              />
            ))}
            {!isDefault && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={addClause}
                  className="rounded-xl border border-dashed border-ff-forest/40 px-3 py-2 text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-forest hover:border-ff-forest transition-colors"
                  data-testid="filter-add-clause"
                >
                  + Clause
                </button>
                <button
                  type="button"
                  onClick={addOrGroup}
                  className="rounded-xl border border-dashed border-ff-accent/40 px-3 py-2 text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-accent hover:border-ff-accent transition-colors"
                  data-testid="filter-add-or-group"
                >
                  + OR group
                </button>
              </div>
            )}
          </div>
        )}

        <FilterPreview
          storeId={storeId}
          configName={config.name}
          filter={config.filter}
        />
      </section>
    </aside>
  );
}

function RowEditor({
  idx,
  row,
  sourceFields,
  onChange,
  onRemove,
  showAndLabel,
}: {
  idx: number;
  row: Row;
  sourceFields: SourceFields;
  onChange: (next: Row) => void;
  onRemove: () => void;
  showAndLabel: boolean;
}) {
  if (row.kind === 'single') {
    return (
      <ClauseRow
        idx={idx}
        clause={row}
        sourceFields={sourceFields}
        onPatch={(patch) => onChange({ ...row, ...patch, kind: 'single' })}
        onRemove={onRemove}
        showAndLabel={showAndLabel}
      />
    );
  }
  return (
    <OrGroupRow
      idx={idx}
      group={row}
      sourceFields={sourceFields}
      onChange={onChange}
      onRemove={onRemove}
      showAndLabel={showAndLabel}
    />
  );
}

function OrGroupRow({
  idx,
  group,
  sourceFields,
  onChange,
  onRemove,
  showAndLabel,
}: {
  idx: number;
  group: OrGroup;
  sourceFields: SourceFields;
  onChange: (next: Row) => void;
  onRemove: () => void;
  showAndLabel: boolean;
}) {
  const clauses = group.clauses.filter((c) => c.kind === 'single') as SingleClause[];

  const replace = (i: number, patch: Partial<SingleClause>) => {
    const next: SingleClause[] = clauses.map((c, j) =>
      j === i ? { ...c, ...patch, kind: 'single' } : c,
    );
    onChange({ kind: 'or', clauses: next });
  };
  const remove = (i: number) => {
    const next = clauses.filter((_, j) => j !== i);
    if (next.length === 0) {
      onRemove();
      return;
    }
    if (next.length === 1) {
      onChange(next[0]);
      return;
    }
    onChange({ kind: 'or', clauses: next });
  };
  const add = () => {
    onChange({
      kind: 'or',
      clauses: [...clauses, emptyClause(sourceFields)],
    });
  };

  return (
    <div
      className="relative rounded-2xl border border-ff-accent/30 bg-ff-accent-soft/20 p-3 space-y-2"
      data-testid={`filter-or-group-${idx}`}
    >
      {showAndLabel && (
        <span className="absolute -top-2 left-3 ff-chip bg-ff-mint text-ff-forest font-mono normal-case text-[10px] py-0.5 px-2">
          AND
        </span>
      )}
      <div className="flex items-center justify-between">
        <span className="ff-chip bg-ff-accent-soft text-ff-accent font-mono normal-case">
          OR group · any clause matches
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove OR group"
          className="text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-accent"
          data-testid={`filter-or-remove-${idx}`}
        >
          Remove
        </button>
      </div>
      <div className="space-y-2">
        {clauses.map((c, i) => (
          <ClauseRow
            key={i}
            idx={i}
            clause={c}
            sourceFields={sourceFields}
            onPatch={(patch) => replace(i, patch)}
            onRemove={() => remove(i)}
            showAndLabel={false}
            orLabel={i > 0}
            testIdPrefix={`filter-or-${idx}-clause`}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full rounded-xl border border-dashed border-ff-accent/40 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-accent hover:border-ff-accent transition-colors"
        data-testid={`filter-or-${idx}-add`}
      >
        + Add OR clause
      </button>
    </div>
  );
}

function ValueInput({
  clause,
  onPatch,
  testIdPrefix,
  idx,
}: {
  clause: SingleClause;
  onPatch: (patch: Partial<SingleClause>) => void;
  testIdPrefix: string;
  idx: number;
}) {
  const op = clause.op;

  if (op === 'is_empty' || op === 'is_not_empty') {
    return (
      <p className="text-[10px] font-mono text-ff-mute italic">
        no value needed for {OP_LABEL[op]}
      </p>
    );
  }

  if (op === 'in' || op === 'not_in') {
    return (
      <ChipInput
        value={clause.value}
        onChange={(v) => onPatch({ value: v })}
        placeholder="value-1, value-2, value-3"
        testId={`${testIdPrefix}-value-${idx}`}
      />
    );
  }

  if (op === 'between') {
    const parts = clause.value.split(',').map((s) => s.trim());
    const [lo = '', hi = ''] = parts;
    return (
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={lo}
          onChange={(e) => onPatch({ value: `${e.target.value}, ${hi}` })}
          placeholder="min"
          className="ff-input text-sm font-mono"
          data-testid={`${testIdPrefix}-value-${idx}-lo`}
        />
        <input
          type="number"
          value={hi}
          onChange={(e) => onPatch({ value: `${lo}, ${e.target.value}` })}
          placeholder="max"
          className="ff-input text-sm font-mono"
          data-testid={`${testIdPrefix}-value-${idx}-hi`}
        />
      </div>
    );
  }

  if (op === 'lt' || op === 'lte' || op === 'gt' || op === 'gte') {
    return (
      <input
        type="number"
        value={clause.value}
        onChange={(e) => onPatch({ value: e.target.value })}
        placeholder="numeric value"
        className="ff-input text-sm font-mono"
        data-testid={`${testIdPrefix}-value-${idx}`}
      />
    );
  }

  if (op === 'regex') {
    let valid = true;
    try { new RegExp(clause.value); } catch { valid = false; }
    return (
      <input
        type="text"
        value={clause.value}
        onChange={(e) => onPatch({ value: e.target.value })}
        placeholder="pattern (case-insensitive)"
        className={`ff-input text-sm font-mono ${valid ? '' : 'ring-2 ring-ff-danger/60'}`}
        data-testid={`${testIdPrefix}-value-${idx}`}
      />
    );
  }

  return (
    <input
      type="text"
      value={clause.value}
      onChange={(e) => onPatch({ value: e.target.value })}
      placeholder="value"
      className="ff-input text-sm font-mono"
      data-testid={`${testIdPrefix}-value-${idx}`}
    />
  );
}

function ClauseRow({
  idx,
  clause,
  sourceFields,
  onPatch,
  onRemove,
  showAndLabel,
  orLabel,
  testIdPrefix,
}: {
  idx: number;
  clause: SingleClause;
  sourceFields: SourceFields;
  onPatch: (patch: Partial<SingleClause>) => void;
  onRemove: () => void;
  showAndLabel: boolean;
  orLabel?: boolean;
  testIdPrefix?: string;
}) {
  const tid = testIdPrefix ?? 'filter';
  const allowedOps = opsFor(clause.field, sourceFields);

  return (
    <div className="rounded-xl border border-ff-forest/20 bg-white p-3 pt-3.5 space-y-2 relative">
      {showAndLabel && (
        <span className="absolute -top-2 left-3 ff-chip bg-ff-mint text-ff-forest font-mono normal-case text-[10px] py-0.5 px-2">
          AND
        </span>
      )}
      {orLabel && (
        <span className="absolute -top-2 left-3 ff-chip bg-ff-accent-soft text-ff-accent font-mono normal-case text-[10px] py-0.5 px-2">
          OR
        </span>
      )}
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <select
          value={clause.field}
          onChange={(e) => {
            const newField = e.target.value;
            const allowed = opsFor(newField, sourceFields);
            const nextOp = allowed.includes(clause.op) ? clause.op : allowed[0];
            onPatch({ field: newField, op: nextOp });
          }}
          className="ff-input text-sm min-w-0 py-2 px-3"
          data-testid={`${tid}-field-${idx}`}
        >
          {sourceFields.from_source.length === 0 && sourceFields.custom.length === 0 && (
            <option value="">(no fields loaded)</option>
          )}
          {sourceFields.from_source.length > 0 && (
            <optgroup label="From the source">
              {sourceFields.from_source.map((f) => (
                <option key={`src-${f}`} value={f}>{f}</option>
              ))}
            </optgroup>
          )}
          {sourceFields.custom.length > 0 && (
            <optgroup label="Custom labels">
              {sourceFields.custom.map((f) => (
                <option key={`cus-${f}`} value={f}>{f}</option>
              ))}
            </optgroup>
          )}
        </select>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove clause"
          className="h-9 w-9 rounded-xl border border-ff-sand/60 bg-white text-ff-mute hover:text-ff-accent hover:border-ff-accent/40 hover:bg-ff-accent-soft/30 transition-colors text-lg leading-none"
          data-testid={`${tid}-remove-${idx}`}
        >
          ×
        </button>
      </div>
      <select
        value={clause.op}
        onChange={(e) => onPatch({ op: e.target.value as FilterOp })}
        className="ff-input text-sm w-full min-w-0 py-2 px-3 font-mono text-ff-forest"
        data-testid={`${tid}-op-${idx}`}
      >
        {allowedOps.map((op) => (
          <option key={op} value={op}>{OP_LABEL[op]}</option>
        ))}
      </select>
      <ValueInput
        clause={clause}
        onPatch={onPatch}
        testIdPrefix={tid}
        idx={idx}
      />
    </div>
  );
}
